import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Cpu,
  Leaf,
  Globe,
  Users,
  Calculator,
  Calendar,
  ChevronDown,
  ChevronUp,
  Menu,
  Lightbulb
} from 'lucide-react';
import Trust from '../Trust';
import Stats from '../Stats';
import ContactForm from '../ContactForm';
import GrantSalesSection from '../GrantSalesSection';

// Dodanie importu czcionki Inter
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const SciezkaSmart = () => {
  const [activeSection, setActiveSection] = useState('intro');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const contactFormRef = useRef(null);

  const scrollToContact = () => {
    if (contactFormRef.current) {
      contactFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (isSidebarOpen) setIsSidebarOpen(false);
  };

  const scrollToSection = (sectionId) => {
    const element = document.querySelector(`[data-section="${sectionId}"]`);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;
      
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      if (isSidebarOpen) setIsSidebarOpen(false);
    }
  };
  
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('[data-section]');
      const scrollPosition = window.scrollY + 200;
      let currentSection = '';
      sections.forEach((section) => {
        if (scrollPosition >= section.offsetTop && scrollPosition < section.offsetTop + section.offsetHeight) {
          currentSection = section.getAttribute('data-section');
        }
      });
      if (currentSection) setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const modules = [
    { icon: <Cpu className="w-6 h-6" />, title: 'Moduł Badań i Rozwoju (B+R)', desc: 'Dofinansowanie na prowadzenie prac zmierzających do stworzenia innowacyjnych produktów, usług lub procesów.' },
    { icon: <TrendingUp className="w-6 h-6" />, title: 'Moduł Wdrożenia innowacji', desc: 'Wsparcie inwestycji niezbędnych do uruchomienia produkcji lub świadczenia usług opartych na innowacyjnych rozwiązaniach.' },
    { icon: <Cpu className="w-6 h-6" />, title: 'Moduł Infrastruktura B+R', desc: 'Finansowanie tworzenia lub rozbudowy wewnętrznych centrów badawczo-rozwojowych w firmie.' },
    { icon: <Cpu className="w-6 h-6" />, title: 'Moduł Cyfryzacja', desc: 'Środki na wdrożenie nowoczesnych technologii cyfrowych w obszarach operacyjnych i zarządczych przedsiębiorstwa.' },
    { icon: <Leaf className="w-6 h-6" />, title: 'Moduł Zazielenienie przedsiębiorców', desc: 'Dofinansowanie inwestycji mających na celu poprawę efektywności energetycznej, redukcję emisji lub inne działania proekologiczne.' },
    { icon: <Globe className="w-6 h-6" />, title: 'Moduł Internacjonalizacja', desc: 'Wsparcie w wejściu na nowe rynki zagraniczne, np. poprzez udział w targach czy misjach gospodarczych.' },
    { icon: <Users className="w-6 h-6" />, title: 'Moduł Kompetencje', desc: 'Dofinansowanie szkoleń podnoszących kwalifikacje pracowników, kluczowych dla realizacji innowacyjnych projektów.' },
  ];

  const fundingUsage = [
    { title: 'Wynagrodzenia', desc: 'Koszty zatrudnienia kadry na umowę o pracę, umowy cywilnoprawne oraz kontrakty B2B bezpośrednio zaangażowanej w projekt.' },
    { title: 'Zewnętrzne Usługi B+R', desc: 'Zakup specjalistycznych usług badawczo-rozwojowych od podwykonawców i jednostek naukowych.' },
    { title: 'Maszyny i Urządzenia', desc: 'Nowoczesne maszyny, urządzenia, linie technologiczne oraz aparatura niezbędna do wdrożenia innowacji.' },
    { title: 'Oprogramowanie i WNiP', desc: 'Specjalistyczne oprogramowanie, licencje, patenty i know-how niezbędne do realizacji projektu.' },
    { title: 'Infrastruktura Badawcza', desc: 'Tworzenie lub rozbudowa centrów B+R, zakup aparatury badawczej, wyposażenie laboratoriów.' },
    { title: 'Materiały i Surowce', desc: 'Materiały do produkcji prototypów i testowych partii produktów w procesie B+R.' },
    { title: 'Budowa/Rozbudowa Obiektów', desc: 'Hale produkcyjne, magazyny, laboratoria niezbędne do realizacji inwestycji.' },
    { title: 'Zielone Inwestycje', desc: 'Instalacje fotowoltaiczne i inne rozwiązania proekologiczne wspierające zrównoważony rozwój.' },
    { title: 'Internacjonalizacja', desc: 'Udział w targach międzynarodowych, doradztwo eksportowe, certyfikaty na rynki zagraniczne.' },
    { title: 'Rozwój Kompetencji', desc: 'Szkolenia w zakresie nowych technologii, cyfryzacji i transformacji zielonej.' },
  ];

  const deadlines = [
    { type: 'MŚP', title: 'Dostępność dla MŚP', dates: '10 kwietnia - 12 czerwca 2025', desc: 'Dla pojedynczych mikro, małych i średnich przedsiębiorstw' },
    { type: 'MŚP', title: 'Ścieżka SMART dla MŚP', dates: '6 maja - 26 czerwca 2025', desc: 'Dla samodzielnych przedsiębiorstw z sektora MŚP' },
    { type: 'Duże', title: 'Dostępność dla dużych', dates: '9 maja - 30 czerwca 2025', desc: 'Dla pojedynczych dużych przedsiębiorstw' },
    { type: 'Konsorcja', title: 'SMART dla konsorcjów', dates: '1 lipca - 30 września 2025', desc: 'Dla konsorcjów firm z organizacjami badawczymi' },
  ];

  const faqs = [
    { question: 'Czy moja firma kwalifikuje się do ubiegania się o dofinansowanie w ramach Ścieżki SMART?', answer: 'Program jest szeroko dostępny dla przedsiębiorstw z całej Polski – mogą o niego aplikować zarówno mikro, małe i średnie firmy (MŚP), jak i duże przedsiębiorstwa. Kluczowe jest prowadzenie działalności gospodarczej na terytorium RP oraz to, aby planowany projekt wpisywał się w cele i moduły programu.' },
    { question: 'Na jakie konkretnie działania lub inwestycje mogę przeznaczyć środki z dofinansowania?', answer: 'Ścieżka SMART finansuje innowacyjne projekty o modułowej strukturze. Środki można przeznaczyć m.in. na prace badawczo-rozwojowe (B+R), wdrożenie wyników B+R, zakup maszyn i urządzeń, oprogramowania, wartości niematerialnych, a także na cyfryzację, zielone inwestycje, działania internacjonalizacyjne (np. targi) oraz rozwój kompetencji pracowników.' },
    { question: 'Jak wysokie dofinansowanie mogę otrzymać na mój projekt?', answer: 'Maksymalna intensywność wsparcia w programie wynosi do 80% kosztów kwalifikowanych projektu, jednak ostateczny poziom dofinansowania jest zróżnicowany w zależności od wybranego modułu (np. B+R ma wyższą intensywność) oraz wielkości Twojej firmy (MŚP zazwyczaj otrzymują wyższe wsparcie niż duże firmy). Nasi konsultanci pomogą precyzyjnie wyliczyć potencjalną kwotę dotacji dla Twojego projektu.' },
    { question: 'Proces składania wniosku wydaje się bardzo skomplikowany. Jak możecie pomóc mojej firmie?', answer: 'Specjalizujemy się w skutecznym pozyskiwaniu dofinansowań. Zapewniamy kompleksowe wsparcie na każdym etapie – od analizy Twojego pomysłu i dopasowania go do wymogów Ścieżki SMART, przez profesjonalne przygotowanie kompletnego wniosku aplikacyjnego, aż po wsparcie w zarządzaniu projektem po uzyskaniu dotacji. Naszym celem jest maksymalne uproszczenie procesu dla Ciebie.' },
    { question: 'Jakie są szanse na uzyskanie dofinansowania i ile trwa cały proces od złożenia wniosku do otrzymania środków?', answer: 'Szanse na uzyskanie dofinansowania zależą od jakości merytorycznej i innowacyjności Twojego projektu oraz od poprawności przygotowania dokumentacji aplikacyjnej. Nasze doświadczenie pozwala znacząco zwiększyć te szanse. Czas oczekiwania na decyzję jest zmienny i zależy od harmonogramu naborów oraz liczby złożonych wniosków, ale zazwyczaj proces oceny trwa kilka miesięcy od daty zakończenia naboru. Wypłata środków następuje po podpisaniu umowy o dofinansowanie i prawidłowej realizacji projektu.' },
  ];
  
  const tableOfContents = [
    { id: 'intro', title: '1. Czym jest Ścieżka SMART?' },
    { id: 'modules', title: '2. Moduły programu' },
    { id: 'eligibility', title: '3. Dla kogo jest program?' },
    { id: 'criteria', title: '4. Kryteria oceny' },
    { id: 'usage', title: '5. Na co wykorzystać środki?' },
    { id: 'funding', title: '6. Poziom dofinansowania' },
    { id: 'deadlines', title: '7. Terminy naborów' },
    { id: 'cooperation', title: '8. Współpraca z nami' },
  ];

  const SectionTitle = ({ children }) => (
    <h2 className="text-3xl lg:text-4xl text-white mb-8 uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
      {children}
    </h2>
  );

  return (
    <div className="min-h-screen bg-black text-white pt-20">
<section className="relative pt-28 sm:pt-32 pb-16 lg:pb-28 overflow-hidden bg-black">
    <div className="absolute inset-0 opacity-10">
        {/* Dekoracyjne kule w tle */}
        <div className="absolute top-20 right-20 w-96 h-96 bg-violet-600/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-violet-700/20 rounded-full blur-3xl animate-pulse animation-delay-2000"></div>

        {/* NOWE IKONY W TLE */}
        <div className="absolute top-1/4 left-[15%] animate-float">
            <Cpu className="w-24 h-24 text-violet-600/40" />
        </div>
        <div className="absolute bottom-1/4 right-[15%] animate-float animation-delay-2000">
            <TrendingUp className="w-32 h-32 text-violet-600/40" />
        </div>
        <div className="absolute top-20 right-[40%] animate-float animation-delay-1000">
            <Lightbulb className="w-20 h-20 text-violet-600/40" />
        </div>
    </div>
    <div className="relative z-10 container mx-auto px-6">
        <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 mb-6 border border-white/20">
                <span className="text-violet-500 text-sm font-medium">Dotacje UE 2025</span>
            </div>
            <h1 
                className="text-3xl sm:text-4xl md:text-6xl text-white mb-6 uppercase leading-tight"
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}
            >
                Ścieżka SMART
            </h1>
            <p className="text-xl text-gray-200 mb-4 font-semibold">
                Dla firm produkcyjnych, przetwórczych oraz sektora IT
            </p>
            <p className="text-lg text-gray-300 mb-8 max-w-3xl mx-auto">
                Z programu Ścieżka SMART sfinansujesz kluczowe inwestycje w rozwój firmy, w tym nowoczesne maszyny, oprogramowanie, licencje, a także usługi badawczo-rozwojowe i doradcze. Możesz także pokryć koszty związane z cyfryzacją, zieloną transformacją, wejściem na rynki zagraniczne oraz szkoleniami dla zespołu.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                    onClick={scrollToContact}
                    className="group flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-violet-600 to-violet-700 text-white font-semibold rounded-full transition-all duration-300 hover:from-violet-700 hover:to-violet-800 hover:scale-105 text-center"
                >
                    <span>Bezpłatna konsultacja</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                    onClick={() => scrollToSection('intro')}
                    className="px-8 py-4 bg-white/10 border border-white/20 text-white font-semibold rounded-full transition-all duration-300 hover:bg-white/20 hover:border-white/30"
                >
                    Dowiedz się więcej
                </button>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="backdrop-blur-md bg-white/5 rounded-xl p-4 text-center border border-white/10">
                    <div className="text-4xl font-black text-violet-500" style={{ fontFamily: 'Inter, sans-serif' }}>4,3 mld €</div>
                    <div className="text-sm text-gray-400 mt-1">Budżet programu</div>
                </div>
                <div className="backdrop-blur-md bg-white/5 rounded-xl p-4 text-center border border-white/10">
                    <div className="text-4xl font-black text-violet-500" style={{ fontFamily: 'Inter, sans-serif' }}>do 80%</div>
                    <div className="text-sm text-gray-400 mt-1">Poziom dofinansowania</div>
                </div>
                <div className="backdrop-blur-md bg-white/5 rounded-xl p-4 text-center border border-white/10">
                    <div className="text-4xl font-black text-violet-500" style={{ fontFamily: 'Inter, sans-serif' }}>7 Modułów</div>
                    <div className="text-sm text-gray-400 mt-1">Elastyczność w projektowaniu</div>
                </div>
            </div>
        </div>
    </div>
    <style>{`
        @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(5deg); }
        }
        .animate-float {
            animation: float 8s ease-in-out infinite;
        }
        .animation-delay-1000 { animation-delay: 1s; }
        .animation-delay-2000 { animation-delay: 2s; }
    `}</style>
</section>

      <Trust />

      <div className="container mx-auto py-20 px-4">
        <div className="relative lg:flex lg:gap-12 lg:items-start">
          
          <main className="lg:flex-1 min-w-0">
            <div data-section="intro" className="mb-16 scroll-mt-24">
              <SectionTitle>1. Czym jest Ścieżka SMART?</SectionTitle>
              <div className="prose prose-invert max-w-none text-gray-300 text-lg space-y-4">
                <p>Ścieżka SMART to kompleksowe działanie w ramach Funduszy Europejskich dla Nowoczesnej Gospodarki (FENG), które zostało stworzone z myślą o przedsiębiorstwach takich jak Twoje. Jego celem jest wsparcie projektów integrujących różnorodne moduły, odpowiadające na kluczowe potrzeby nowoczesnej firmy.</p>
                <p>Program Ścieżka SMART to kompleksowe narzędzie wspierające strategiczny rozwój firm poprzez dofinansowanie kluczowych obszarów ich działalności. Został on zaprojektowany w formie modułowej, pozwalając przedsiębiorcom na elastyczne dopasowanie wsparcia do swoich indywidualnych potrzeb.</p>
                <div className="p-6 backdrop-blur-md bg-white/5 rounded-xl border border-violet-600/30">
                  <p className="text-violet-500 font-semibold mb-2">Kluczowe informacje:</p>
                  <p>Na finansowanie projektów w ramach Ścieżki SMART przeznaczono łącznie aż <strong className="text-white">4,3 mld euro dotacji</strong>, przy czym minimalna wartość projektu musi przekroczyć <strong className="text-white">1 mln PLN</strong> (w przypadku projektów z modułem B+R).</p>
                </div>
              </div>
            </div>

            <div data-section="modules" className="mb-16 scroll-mt-24">
              <SectionTitle>2. Moduły programu</SectionTitle>
              <p className="text-gray-300 mb-8 text-lg">Poniżej przedstawiamy 7 głównych obszarów, w ramach których możesz uzyskać dofinansowanie:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {modules.map((module) => (
                  <div key={module.title} className="backdrop-blur-md bg-white/5 rounded-xl p-6 border border-white/10 hover:border-violet-600/50 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-lg flex items-center justify-center text-violet-600">
                        {module.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-white text-lg mb-2">{module.title}</h3>
                        <p className="text-gray-400 text-sm">{module.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div data-section="eligibility" className="mb-16 scroll-mt-24">
              <SectionTitle>3. Dla kogo jest Ścieżka SMART?</SectionTitle>
              <p className="text-gray-300 mb-8 text-lg">Ścieżka SMART jest programem skierowanym do przedsiębiorstw prowadzących działalność gospodarczą na terytorium Rzeczypospolitej Polskiej. O dofinansowanie mogą ubiegać się firmy każdej wielkości:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="backdrop-blur-md bg-white/5 rounded-xl p-6 border border-white/10">
                  <h3 className="text-xl font-bold text-violet-500 mb-3">Mikro, małe i średnie przedsiębiorstwa</h3>
                  <p className="text-gray-300">Firmy z sektora MŚP, zatrudniające poniżej 250 pracowników, stanowią kluczową grupę docelową programu.</p>
                </div>
                <div className="backdrop-blur-md bg-white/5 rounded-xl p-6 border border-white/10">
                  <h3 className="text-xl font-bold text-violet-500 mb-3">Duże przedsiębiorstwa i konsorcja</h3>
                  <p className="text-gray-300">Firmy zatrudniające powyżej 250 osób oraz projekty realizowane wspólnie z innymi firmami lub jednostkami naukowymi.</p>
                </div>
              </div>
            </div>

            <div data-section="criteria" className="mb-16 scroll-mt-24">
              <SectionTitle>4. Kryteria oceny</SectionTitle>
              <p className="text-gray-300 mb-8 text-lg">Ocena projektu opiera się na kilku istotnych elementach, które decydują o szansach na uzyskanie dofinansowania:</p>
              <div className="space-y-6">
                <div className="backdrop-blur-md bg-white/5 rounded-xl p-6 border border-white/10">
                  <h3 className="text-xl font-bold text-violet-500 mb-4">Moduły Obowiązkowe</h3>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" /><span>Każdy projekt musi zawierać co najmniej jeden z dwóch modułów: <strong className="text-white">Moduł B+R</strong> lub <strong className="text-white">Moduł Wdrożenia innowacji</strong>.</span></li>
                    <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" /><span>Dla <strong className="text-white">dużych przedsiębiorstw</strong> realizacja Modułu B+R jest <strong className="text-white">obligatoryjna</strong>.</span></li>
                    <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" /><span><strong className="text-white">MŚP</strong> mogą elastycznie wybrać między Modułem B+R a Modułem Wdrożenia innowacji.</span></li>
                  </ul>
                </div>
                <div className="backdrop-blur-md bg-white/5 rounded-xl p-6 border border-white/10">
                  <h3 className="text-xl font-bold text-violet-500 mb-3">Zgodność z Krajową Inteligentną Specjalizacją (KIS)</h3>
                  <p className="text-gray-300">Przedmiot projektu musi wpisywać się w zakres tematyczny określony w KIS. Są to obszary priorytetowe dla rozwoju polskiej gospodarki, koncentrujące się na innowacjach.</p>
                </div>
                <div className="backdrop-blur-md bg-white/5 rounded-xl p-6 border border-white/10">
                  <h3 className="text-xl font-bold text-violet-500 mb-3">Innowacyjność Projektu</h3>
                  <p className="text-gray-300">Projekt musi skutkować innowacją na poziomie co najmniej krajowym. Najwyżej punktowane są projekty, w których innowacja została wykreowana w ramach Modułu B+R.</p>
                </div>
              </div>
            </div>

            <div data-section="usage" className="mb-16 scroll-mt-24">
                <SectionTitle>5. Na co można wykorzystać środki?</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fundingUsage.map((item) => (
                    <div key={item.title} className="backdrop-blur-md bg-white/5 rounded-xl p-5 border border-white/10 hover:border-violet-600/50 transition-all duration-300">
                    <h3 className="font-bold text-violet-500 text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                ))}
                </div>
            </div>

            <div data-section="funding" className="mb-16 scroll-mt-24">
                <SectionTitle>6. Poziom dofinansowania</SectionTitle>
                <div className="backdrop-blur-md bg-white/5 rounded-xl p-5 sm:p-8 border border-white/10 text-lg space-y-4">
                  <p className="text-gray-300">Masz innowacyjny pomysł na rozwój firmy? Świetnie! W ramach programu Ścieżka SMART masz szansę na naprawdę znaczące wsparcie finansowe. Do podziału jest imponująca kwota <strong className="text-white">4,3 miliarda euro</strong>, co otwiera ogromne możliwości dla polskich przedsiębiorców.</p>
                  <p className="text-gray-300">Choć maksymalny poziom dofinansowania może sięgnąć nawet <strong className="text-white">80% kosztów kwalifikowanych</strong> Twojego projektu, ostateczna wysokość dotacji zależy od konkretnych modułów, o które aplikujesz.</p>
                  <div className="bg-gradient-to-r from-violet-600/10 to-violet-700/10 rounded-lg p-6 border border-violet-600/20">
                    <p className="text-gray-300 mb-4">Nie martw się skomplikowanymi wyliczeniami – nasi doświadczeni konsultanci przeanalizują zakres i budżet Twojego przedsięwzięcia, a następnie precyzyjnie określą, na jakie wsparcie finansowe możesz liczyć.</p>
                    <button
                        onClick={scrollToContact}
                        className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-full transition-all duration-300 font-semibold border border-white/20"
                    >
                        Sprawdź swój potencjał
                    </button>
                  </div>
                </div>
            </div>
            
            <div data-section="deadlines" className="mb-16 scroll-mt-24">
                <SectionTitle>7. Terminy naborów w 2025</SectionTitle>
                <p className="text-gray-300 mb-8 text-lg">Program Ścieżka SMART prowadzony jest w ramach kilku naborów, z których każdy ma określony harmonogram i skierowany jest do innych typów wnioskodawców:</p>
                <div className="space-y-4">
                    {deadlines.map((deadline, index) => (
                    <div key={index} className="backdrop-blur-md bg-white/5 rounded-xl p-6 border border-white/10 hover:border-violet-600/50 transition-all duration-300">
                        <div className="flex items-start justify-between flex-wrap gap-4">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                deadline.type === 'MŚP' ? 'bg-violet-600/20 text-violet-500' :
                                deadline.type === 'Duże' ? 'bg-amber-500/20 text-amber-400' :
                                'bg-green-500/20 text-green-400'
                            }`}>
                                {deadline.type}
                            </span>
                            <h3 className="text-xl font-semibold text-white">{deadline.title}</h3>
                            </div>
                            <p className="text-gray-400 mb-2">{deadline.desc}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <div className="flex items-center gap-2 text-violet-500">
                            <Calendar className="w-5 h-5" />
                            <span className="font-semibold">{deadline.dates}</span>
                            </div>
                        </div>
                        </div>
                    </div>
                    ))}
                </div>
            </div>
            
            <div data-section="cooperation" className="mb-16 scroll-mt-24">
                <SectionTitle>8. Współpraca z Szynkowski & Company</SectionTitle>
                <div className="prose prose-invert max-w-none text-gray-300 text-lg space-y-4">
                    <p>Rozumiemy, że złożoność programów unijnych, takich jak Ścieżka SMART, może wydawać się przytłaczająca. Twoja firma ma innowacyjny potencjał i zasługuje na najlepsze wsparcie, aby sięgnąć po dostępne fundusze. Właśnie w tym miejscu wkracza Szynkowski & Company – Twój zaufany partner w skutecznym pozyskiwaniu dofinansowania.</p>
                    <p>Nasza współpraca to przemyślany proces, który ma na celu maksymalizację Twoich szans i minimalizację Twojego zaangażowania w zawiłe procedury. Nie musisz stawać sam na sam z gąszczem dokumentów i wytycznych. Zaczynamy od szczegółowej rozmowy o Twoim pomyśle i celach biznesowych – to Ty jesteś ekspertem w swojej branży, a my w świecie dotacji. Nasz zespół analizuje potencjał Twojego projektu, dopasowując go idealnie do wymogów i modułów Ścieżki SMART, identyfikując najlepszą ścieżkę aplikacyjną.</p>
                    <p>Kluczowym etapem jest profesjonalne przygotowanie wniosku. To tutaj nasze bezcenne doświadczenie i znajomość specyfiki programu przekładają się na konkretne działania. Tworzymy kompletną i przekonującą dokumentację, precyzyjnie kalkulujemy budżet i dbamy o każdy detal, który ma znaczenie dla oceny Twojego projektu. Przejmujemy na siebie ciężar formalności, pozwalając Ci skupić się na tym, co robisz najlepiej – rozwijaniu swojej firmy.</p>
                    <div className="p-6 bg-gradient-to-r from-violet-600/10 to-violet-700/10 rounded-xl border border-violet-600/20 mt-6">
                        <p>Jesteśmy z Tobą na każdym kroku – od pierwszej analizy, przez strategiczne planowanie, aż po złożenie wniosku i dalsze etapy oceny. Naszym celem jest Twój sukces. Wybierając Szynkowski & Company, zyskujesz nie tylko eksperckie wsparcie, ale przede wszystkim spokój i pewność, że proces aplikacyjny jest w najlepszych rękach.</p>
                    </div>
                </div>
            </div>
          </main>

          <aside className="hidden lg:block lg:w-80 xl:w-96 flex-shrink-0">
            <div className="sticky top-24">
              <div className="backdrop-blur-md bg-white/5 rounded-xl p-5 sm:p-8 border border-white/10">
                <h3 className="text-xl font-semibold mb-6 text-white">Spis treści</h3>
                <nav className="space-y-3">
                  {tableOfContents.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`block w-full text-left px-4 py-2 rounded-lg transition-all duration-300 font-medium ${
                        activeSection === item.id
                          ? 'bg-gradient-to-r from-violet-600/20 to-violet-700/20 text-white border-l-4 border-violet-600'
                          : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {item.title}
                    </button>
                  ))}
                </nav>
                <div className="mt-8 p-6 bg-gradient-to-r from-violet-600/10 to-violet-700/10 rounded-lg border border-violet-600/20">
                  <h4 className="text-lg font-semibold text-white mb-3">Potrzebujesz pomocy?</h4>
                  <p className="text-gray-400 text-sm mb-4">Skontaktuj się z naszymi ekspertami i dowiedz się więcej o możliwościach dla Twojej firmy.</p>
                  <button
                    onClick={scrollToContact}
                    className="w-full group flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-violet-600 to-violet-700 text-white font-semibold rounded-full transition-all duration-300 hover:from-violet-700 hover:to-violet-800 hover:scale-105"
                  >
                    <span>Umów konsultację</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
      
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="lg:hidden fixed bottom-8 right-8 z-50 w-14 h-14 bg-gradient-to-r from-violet-600 to-violet-700 rounded-full shadow-lg flex items-center justify-center text-white"
      >
        <Menu className="w-6 h-6" />
      </button>

      <Stats />
      
      <section className="py-16 md:py-24 bg-black">
        <div className="container mx-auto max-w-4xl px-6">
          <SectionTitle>Najczęściej zadawane pytania</SectionTitle>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="backdrop-blur-md bg-white/5 rounded-xl border border-white/10 overflow-hidden transition-all duration-300 hover:border-violet-600/30">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between group"
                >
                  <span className="font-semibold text-lg text-white pr-4">{faq.question}</span>
                  {expandedFaq === index ? 
                    <ChevronUp className="w-5 h-5 text-violet-600 flex-shrink-0" /> :
                    <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-violet-600 transition-colors flex-shrink-0" />
                  }
                </button>
                <div className={`transition-all duration-500 ease-in-out ${expandedFaq === index ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="overflow-hidden px-6 pb-4">
                    <p className="text-gray-300 pt-2">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GrantSalesSection />
      <Trust />

      <div id="contact-form-section" ref={contactFormRef} className="scroll-mt-20">
        <ContactForm />
      </div>

       {isSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/95 overflow-y-auto" onClick={() => setIsSidebarOpen(false)}>
          <div className="backdrop-blur-md bg-white/5 rounded-lg p-6 max-w-md mx-auto my-20 border border-white/10" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-semibold mb-6 text-white">Spis treści</h3>
            <nav className="space-y-3">
              {tableOfContents.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left px-4 py-2 rounded-lg transition-all duration-300 font-medium ${
                    activeSection === item.id
                      ? 'bg-gradient-to-r from-violet-600/20 to-violet-700/20 text-white border-l-4 border-violet-600'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {item.title}
                </button>
              ))}
            </nav>
          </div>
        </div>
       )}
       <style>{`.max-h-96 { max-height: 24rem; } .animation-delay-2000 { animation-delay: 2s; }`}</style>
    </div>
  );
};

export default SciezkaSmart;
